import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  getAdminDashboardEconomyStats,
  getAdminDashboardReadingStats,
  getAdminDashboardQueueStats,
  getAdminRecentPurchases,
  getTopTokenBalances,
  getAdminQuotaOverview,
  getAdminTopContent,
  getAdminLogSummary,
  getCreatorAccessViolations,
  getContentPerformanceStats,
  type Economy24hStats,
  type Reading24hStats,
  type QueueStats,
  type RecentPurchaseItem,
  type QuotaOverviewStats,
  type TopContentItem,
  type LogSummaryStats,
  type StatRange,
  type TopContentScope,
  type CreatorAccessViolationStats,
  type ContentPerformanceStats,
} from "../api/adminStatsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";

const STAT_RANGE_OPTIONS: Array<{ value: StatRange; label: string }> = [
  { value: "24h", label: "Son 24 Saat" },
  { value: "all", label: "Genel Toplam" },
];

type SectionKey = "purchases" | "balances" | "topReads" | "logs";

const SECTION_TITLES: Record<SectionKey, string> = {
  purchases: "Satin Alma Akisi",
  balances: "Bakiye Gorunumu",
  topReads: "En Cok Okunanlar",
  logs: "Log Ozeti",
};

const INITIAL_SECTION_STATE: Record<SectionKey, boolean> = {
  purchases: false,
  balances: false,
  topReads: false,
  logs: false,
};

export default function AdminDashboard() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("Dashboard.View");

  const [economy, setEconomy] = useState<Economy24hStats | null>(null);
  const [reading, setReading] = useState<Reading24hStats | null>(null);
  const [queue, setQueue] = useState<QueueStats | null>(null);
  const [recentPurchases, setRecentPurchases] = useState<RecentPurchaseItem[]>([]);
  const [topBalances, setTopBalances] = useState<Array<{ username: string; balanceZ: number }>>([]);
  const [quotaOverview, setQuotaOverview] = useState<QuotaOverviewStats | null>(null);
  const [topContent, setTopContent] = useState<TopContentItem[]>([]);
  const [logSummary, setLogSummary] = useState<LogSummaryStats | null>(null);
  const [creatorViolations, setCreatorViolations] = useState<CreatorAccessViolationStats | null>(null);
  const [contentPerformance, setContentPerformance] = useState<ContentPerformanceStats | null>(null);

  const [statRange, setStatRange] = useState<StatRange>("24h");
  const [topContentScope, setTopContentScope] = useState<TopContentScope>("content");
  const [sectionsOpen, setSectionsOpen] = useState<Record<SectionKey, boolean>>(INITIAL_SECTION_STATE);
  const [balanceFilter, setBalanceFilter] = useState("");

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const loadStaticBlocks = async () => {
      const [purchases, balances, quota] = await Promise.all([
        getAdminRecentPurchases(),
        getTopTokenBalances(),
        getAdminQuotaOverview(),
      ]);

      setRecentPurchases(purchases);
      setTopBalances(balances);
      setQuotaOverview(quota);
    };

    void loadStaticBlocks();
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const loadDynamicBlocks = async () => {
      const [eco, read, queueStats, content, log, violations, performance] = await Promise.all([
        getAdminDashboardEconomyStats(statRange),
        getAdminDashboardReadingStats(statRange),
        getAdminDashboardQueueStats(statRange),
        getAdminTopContent(statRange, topContentScope),
        getAdminLogSummary(statRange),
        getCreatorAccessViolations(statRange),
        getContentPerformanceStats(),
      ]);

      setEconomy(eco);
      setReading(read);
      setQueue(queueStats);
      setTopContent(content);
      setLogSummary(log);
      setCreatorViolations(violations);
      setContentPerformance(performance);
    };

    void loadDynamicBlocks();
  }, [allowed, statRange, topContentScope]);

  const rangeLabel = statRange === "24h" ? "Son 24 Saat" : "Genel Toplam";

  const filteredBalances = useMemo(() => {
    if (!balanceFilter.trim()) {
      return topBalances;
    }

    const term = balanceFilter.toLowerCase();
    return topBalances.filter((entry) => entry.username.toLowerCase().includes(term));
  }, [balanceFilter, topBalances]);

  const toggleSection = (key: SectionKey) => {
    setSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const alerts: Array<{ tone: "warning" | "danger"; title: string; body: string; actionHref: string; actionLabel: string }> =
    useMemo(() => {
      const list: Array<{ tone: "warning" | "danger"; title: string; body: string; actionHref: string; actionLabel: string }> = [];

      if ((queue?.pendingChaptersCount ?? 0) > 0) {
        list.push({
          tone: "warning",
          title: "Onay bekleyen bolumler",
          body: `${queue?.pendingChaptersCount ?? 0} bolum inceleme bekliyor.`,
          actionHref: "/admin/content-360?tab=content",
          actionLabel: "Kuyruga git",
        });
      }

      if ((logSummary?.errorCount ?? 0) > 0) {
        list.push({
          tone: "danger",
          title: "Kritik hata loglari",
          body: `${logSummary?.errorCount ?? 0} hata kaydi var. Son olaylari kontrol et.`,
          actionHref: "/admin/ops?tab=logs",
          actionLabel: "Loglari ac",
        });
      }

      if ((creatorViolations?.total ?? 0) > 0) {
        list.push({
          tone: "danger",
          title: "Creator erisim ihlali",
          body: `${creatorViolations?.total ?? 0} basarisiz yetki denemesi kaydedildi.`,
          actionHref: "/admin/ops?tab=logs",
          actionLabel: "Detaylari incele",
        });
      }

      if (quotaOverview && quotaOverview.activePackageUsers < Math.max(1, quotaOverview.totalUsers / 4)) {
        list.push({
          tone: "warning",
          title: "Paket penetrasyonu dusuk",
          body: `Aktif paketli kullanici: ${quotaOverview.activePackageUsers}/${quotaOverview.totalUsers}.`,
          actionHref: "/admin/finance?tab=sales-ledger",
          actionLabel: "Gelir verisini incele",
        });
      }

      return list;
    }, [queue?.pendingChaptersCount, logSummary?.errorCount, quotaOverview, creatorViolations?.total]);

  if (checkingPermission) {
    return <div className="workspace-panel rounded-2xl p-8 text-center text-sm text-slate-600">Yetkiler kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="admin-soft-alert text-sm">Bu sayfaya erisim yetkin bulunmuyor.</div>;
  }

  return (
    <div className="space-y-5">
      <section className="workspace-panel rounded-3xl p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Operasyon Ozeti</h2>
            <p className="text-xs text-slate-600">Gosterim araligi: {rangeLabel}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border border-slate-200 bg-white/85 p-1 text-xs font-semibold">
              {STAT_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatRange(option.value)}
                  className={`rounded-full px-3 py-1 transition ${
                    statRange === option.value ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <a
              href="/admin/platform?tab=help"
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Yardim
            </a>
          </div>
        </div>
      </section>

      {alerts.length > 0 && (
        <section className="grid gap-3 md:grid-cols-2">
          {alerts.map((alert, idx) => (
            <AlertTile key={`${alert.title}-${idx}`} {...alert} />
          ))}
        </section>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title={`Ekonomi (${rangeLabel})`}
          rows={[
            { label: "Satilan paket", value: economy?.totalPackagesSold ?? 0 },
            { label: "Populer paket", value: economy?.mostPopularPackageName ?? "-" },
            { label: "Harcanan Z", value: economy?.totalZTokensSpent ?? 0 },
            { label: "Tekli bolum acma", value: economy?.singleUnlockCount ?? 0 },
          ]}
        />
        <StatCard
          title={`Okuma Aktivitesi (${rangeLabel})`}
          rows={[
            { label: "Toplam okuma", value: reading?.totalReads ?? 0 },
            { label: "Premium okuma", value: reading?.premiumReads ?? 0 },
            { label: "Ucretsiz onizleme", value: reading?.freePreviewReads ?? 0 },
          ]}
        />
        <StatCard
          title="Kuyruk ve Yayin"
          rows={[
            { label: "Bekleyen bolum", value: queue?.pendingChaptersCount ?? 0 },
            {
              label: statRange === "24h" ? "Son 24 saatte yayinlanan" : "Toplam yayinlanan",
              value: queue?.chaptersPublishedInRange ?? 0,
            },
          ]}
        />
        <StatCard
          title="Kota Kullanimi"
          rows={[
            { label: "Ucretsiz kota tuketimi", value: quotaOverview?.freeQuotaUsed ?? 0 },
            { label: "Premium kota tuketimi", value: quotaOverview?.premiumQuotaUsed ?? 0 },
            { label: "Kayit sayisi", value: quotaOverview?.usageEntries ?? 0 },
            { label: "Aktif paketli kullanici", value: quotaOverview?.activePackageUsers ?? 0 },
          ]}
        />
      </section>

      {creatorViolations && (
        <section className="workspace-panel rounded-2xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-slate-800">Creator erisim ihlalleri</div>
              <p className="text-xs text-slate-500">
                Aralik: {creatorViolations.range === "all" ? "Genel Toplam" : "Son 24 Saat"}
              </p>
            </div>
            <div className="rounded-full border border-rose-200 bg-rose-50 px-4 py-1 text-sm font-semibold text-rose-700">
              {creatorViolations.total} kayit
            </div>
          </div>

          <ul className="mt-4 space-y-2">
            {creatorViolations.recent.length === 0 && (
              <li className="text-xs text-slate-500">Bu aralikta creator erisim ihlali kaydi yok.</li>
            )}
            {creatorViolations.recent.map((entry) => (
              <li key={entry.id} className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-sm">
                <div className="font-semibold text-slate-800">{entry.actorName ?? "Bilinmeyen kullanici"}</div>
                <div className="text-xs text-slate-500">{entry.path ?? "/"} - {entry.actorIp ?? "IP yok"}</div>
                <div className="text-xs text-slate-500">{new Date(entry.occurredAtUtc).toLocaleString("tr-TR")}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <AccordionSection
        title={SECTION_TITLES.purchases}
        isOpen={sectionsOpen.purchases}
        onToggle={() => toggleSection("purchases")}
      >
        {recentPurchases.length === 0 && <div className="text-sm text-slate-500">Henuz kayit bulunamadi.</div>}
        {recentPurchases.length > 0 && (
          <div className="divide-y divide-slate-100 text-sm">
            {recentPurchases.map((purchase, idx) => (
              <div key={`${purchase.username}-${idx}`} className="flex flex-wrap items-center gap-2 py-2">
                <div className="w-full font-semibold text-slate-800 md:w-1/4">{purchase.username}</div>
                <div className="w-1/3 text-slate-600 md:w-1/4">{purchase.itemType === "package" ? "Paket" : "Tek bolum"}</div>
                <div className="w-1/3 text-slate-600 md:w-1/4">{purchase.amountZ} Z</div>
                <div className="w-full text-xs text-slate-500 md:w-1/4 md:text-right">
                  {new Date(purchase.timestamp).toLocaleString("tr-TR")}
                </div>
              </div>
            ))}
          </div>
        )}
      </AccordionSection>

      <AccordionSection
        title={SECTION_TITLES.balances}
        isOpen={sectionsOpen.balances}
        onToggle={() => toggleSection("balances")}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="balance-filter">
            Kullanici ara
          </label>
          <input
            id="balance-filter"
            type="text"
            value={balanceFilter}
            onChange={(event) => setBalanceFilter(event.target.value)}
            placeholder="Isim ara"
            className="flex-1 px-3 py-2 text-sm"
          />
        </div>

        {filteredBalances.length === 0 && <div className="text-sm text-slate-500">Eslesen kullanici bulunamadi.</div>}

        {filteredBalances.length > 0 && (
          <div className="space-y-2 text-sm">
            {filteredBalances.map((entry) => (
              <div key={entry.username} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                <span className="font-semibold text-slate-800">{entry.username}</span>
                <span className="text-slate-700">{entry.balanceZ} Z</span>
              </div>
            ))}
          </div>
        )}
      </AccordionSection>

      <AccordionSection
        title={SECTION_TITLES.topReads}
        isOpen={sectionsOpen.topReads}
        onToggle={() => toggleSection("topReads")}
      >
        <div className="mb-3 flex items-center gap-2 text-sm">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="top-content-scope">
            Gosterim
          </label>
          <select
            id="top-content-scope"
            value={topContentScope}
            onChange={(event) => setTopContentScope(event.target.value as TopContentScope)}
            className="px-3 py-2 text-sm"
          >
            <option value="content">Icerikler</option>
            <option value="part">Bolumler</option>
          </select>
        </div>

        {topContent.length === 0 && <div className="text-sm text-slate-500">Bu aralik icin okuma verisi bulunamadi.</div>}

        {topContent.length > 0 && (
          <ol className="space-y-2 text-sm">
            {topContent.map((item) => (
              <li key={item.id} className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 pr-3">
                    <div className="truncate font-semibold text-slate-800">{item.title}</div>
                    {item.subtitle && <div className="truncate text-xs text-slate-500">{item.subtitle}</div>}
                  </div>
                  <div className="shrink-0 text-xs font-semibold text-slate-600">{item.readCount} okuma</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </AccordionSection>

      <AccordionSection
        title={SECTION_TITLES.logs}
        isOpen={sectionsOpen.logs}
        onToggle={() => toggleSection("logs")}
      >
        {!logSummary && <div className="text-sm text-slate-500">Log verisi alinamadi.</div>}

        {logSummary && (
          <>
            <div className="grid grid-cols-3 gap-2 text-center text-xs font-semibold">
              <LogBadge label="Bilgi" value={logSummary.infoCount} tone="text-slate-600 bg-slate-100" />
              <LogBadge label="Uyari" value={logSummary.warningCount} tone="text-amber-700 bg-amber-100" />
              <LogBadge label="Hata" value={logSummary.errorCount} tone="text-rose-700 bg-rose-100" />
            </div>
            <div className="mt-3 text-xs text-slate-500">Toplam {logSummary.total} kayit</div>

            <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">One cikan alanlar</div>
            <ul className="mt-1 space-y-1 text-sm">
              {logSummary.topAreas.length === 0 && <li className="text-slate-400">Veri yok</li>}
              {logSummary.topAreas.map((area) => (
                <li key={area.area} className="flex items-center justify-between">
                  <span>{area.area}</span>
                  <span className="text-xs text-slate-500">{area.count}</span>
                </li>
              ))}
            </ul>

            {logSummary.latestCritical && (
              <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                <div className="font-semibold">Son kritik olay</div>
                <div>{new Date(logSummary.latestCritical.occurredAtUtc).toLocaleString("tr-TR")}</div>
                <div>
                  {logSummary.latestCritical.area} / {logSummary.latestCritical.action}
                </div>
                <div>
                  {logSummary.latestCritical.actorName ?? "Sistem"} - Status {logSummary.latestCritical.statusCode ?? "-"}
                </div>
              </div>
            )}
          </>
        )}
      </AccordionSection>

      {contentPerformance && contentPerformance.series.length > 0 && (
        <section className="workspace-panel rounded-2xl p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-base font-semibold text-slate-800">Seri performansi</div>
              <p className="text-xs text-slate-500">Son {contentPerformance.days} gunde okuma ve gelir dagilimi.</p>
            </div>
            <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-1 text-sm font-semibold text-slate-700">
              {contentPerformance.series.length} seri
            </div>
          </div>

          <div className="space-y-4">
            {contentPerformance.series.map((serie) => (
              <div key={serie.contentId} className="rounded-2xl border border-slate-200 bg-white/80 p-3">
                <div className="mb-2 text-sm font-semibold text-slate-800">{serie.title}</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="px-2 py-1">Tarih</th>
                        <th className="px-2 py-1">Okuma</th>
                        <th className="px-2 py-1">Premium</th>
                        <th className="px-2 py-1">Ucretsiz</th>
                        <th className="px-2 py-1">Satin alim</th>
                        <th className="px-2 py-1">Gelir (Z)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serie.data.map((point) => (
                        <tr key={`${serie.contentId}-${point.date}`} className="border-t border-slate-100">
                          <td className="px-2 py-1">{point.date}</td>
                          <td className="px-2 py-1">{point.readCount}</td>
                          <td className="px-2 py-1">{point.premiumReadCount}</td>
                          <td className="px-2 py-1">{point.freePreviewCount}</td>
                          <td className="px-2 py-1">{point.purchaseCount}</td>
                          <td className="px-2 py-1">{point.revenue.toLocaleString("tr-TR")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ title, rows }: { title: string; rows: Array<{ label: string; value: string | number }> }) {
  return (
    <div className="workspace-panel rounded-2xl p-4 text-sm">
      <div className="mb-2 font-semibold text-slate-900">{title}</div>
      <dl className="space-y-1 text-slate-700">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between">
            <dt className="text-xs text-slate-500">{row.label}</dt>
            <dd className="font-semibold">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function AccordionSection({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className="workspace-panel rounded-2xl">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-slate-700"
      >
        <span>{title}</span>
        <span className="text-xs text-slate-500">{isOpen ? "-" : "+"}</span>
      </button>
      {isOpen && <div className="border-t border-slate-100 px-4 py-4">{children}</div>}
    </section>
  );
}

function LogBadge({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={`rounded-xl py-3 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function AlertTile({
  tone,
  title,
  body,
  actionHref,
  actionLabel,
}: {
  tone: "warning" | "danger";
  title: string;
  body: string;
  actionHref: string;
  actionLabel: string;
}) {
  const classes =
    tone === "danger"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-amber-200 bg-amber-50 text-amber-800";

  return (
    <article className={`rounded-2xl border p-3 text-sm ${classes}`}>
      <div className="font-semibold">{title}</div>
      <div className="mt-1 text-xs">{body}</div>
      <a href={actionHref} className="mt-2 inline-flex rounded-lg border px-3 py-1 text-xs font-semibold hover:underline">
        {actionLabel}
      </a>
    </article>
  );
}
